import type { Producto } from '../types';

export function isProductoArchivado(p: Pick<Producto, 'estado'> & { fechaArchivado?: Date | string | null }): boolean {
  if (String(p.estado || '').toUpperCase() === 'ARCHIVADO') return true;
  const fa = p.fechaArchivado;
  return fa != null && String(fa).trim() !== '';
}

/** Sellable in facturas, ventas, catálogo. */
export function isProductoVendible(p: Pick<Producto, 'estado'> & { fechaArchivado?: Date | string | null }): boolean {
  if (isProductoArchivado(p)) return false;
  const e = String(p.estado || '').toUpperCase();
  return e === 'ACTIVO' || e === 'AGOTADO';
}

/** Stock cell label: archived products show Borrado, not 0. */
export function labelProductoStockDisplay(
  p: Pick<Producto, 'estado' | 'stock'> & {
    inventory_type?: string;
    fechaArchivado?: Date | string | null;
  }
): string {
  if (isProductoArchivado(p)) return 'Borrado';
  if (p.inventory_type === 'unlimited') return '—';
  return String(p.stock ?? 0);
}

const BORRADO_RE = /^(borrado|producto\s+eliminado|producto\s+no\s+disponible)$/i;

/** Historical factura line: show Borrado instead of empty/zero for archived products. */
export function labelFacturaItemProducto(item: {
  producto?: string;
  cantidad?: number;
  productoEstado?: string | null;
  fechaArchivado?: Date | string | null;
}): string {
  const nombre = String(item.producto || '').trim();
  if (item.productoEstado && String(item.productoEstado).toUpperCase() === 'ARCHIVADO') return 'Borrado';
  if (item.fechaArchivado) return 'Borrado';
  if (!nombre && (item.cantidad ?? 0) === 0) return 'Borrado';
  if (BORRADO_RE.test(nombre)) return 'Borrado';
  return nombre || 'Producto';
}

export function labelFacturaItemCantidad(item: {
  producto?: string;
  cantidad?: number;
  productoEstado?: string | null;
  fechaArchivado?: Date | string | null;
}): string | number {
  const label = labelFacturaItemProducto(item);
  if (label === 'Borrado') return '—';
  return item.cantidad ?? 0;
}

export function resolveProductoEstadoFromDb(
  p: {
    estado?: string | null;
    activo?: boolean | null;
    stock?: number | null;
    fecha_archivado?: string | null;
  },
  inventoryType: 'tracked' | 'unlimited'
): string {
  if (p.fecha_archivado) return 'ARCHIVADO';
  const dbEstado = String(p.estado || '').trim().toUpperCase();
  if (dbEstado === 'ARCHIVADO' || dbEstado === 'DESCONTINUADO') return dbEstado;
  if (dbEstado === 'AGOTADO') return 'AGOTADO';

  const esActivo = p.activo !== false;

  if (dbEstado === 'INACTIVO' || !esActivo) {
    if (inventoryType === 'tracked' && (p.stock ?? 0) === 0 && esActivo) return 'AGOTADO';
    return dbEstado || 'INACTIVO';
  }
  if (inventoryType === 'unlimited') {
    return 'ACTIVO';
  }
  if ((p.stock ?? 0) === 0) return 'AGOTADO';
  return 'ACTIVO';
}
